import { useEffect, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";

import { updateSellerLocation } from "src/api/lalamove";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import CustomMap from "src/components/ui/Maps/CustomMaps";
import Loading from "src/components/ui/Loading";

import font from "src/styles/font";
import { Black, Red } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";
import { useAuth } from "src/hooks/useAuth";

const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.md};
  font-weight: 500;
  color: ${Black};

  span {
    font-size: ${font.size.md};
    font-weight: 500;
    color: ${Red};
  }
`;

const WrapperMapsContainer = styled.div`
  width: 100%;
  padding-top: calc(100% / (16 / 9));
  position: relative;
`;

const MapsContainer = styled.div`
  position: absolute;
  border: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 16px;
`;

const AddLocationDialog = ({ sellerDetail, onClose, onSuccess }) => {
  const { checkAllowPermission } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();

  const [isLoading, setIsLoading] = useState(false);

  const [addressData, setAddressData] = useState({
    coordinates: {
      lat: null,
      lng: null,
    },
    address: "",
  });

  useEffect(() => {
    if (!sellerDetail) return;

    let coordinates = addressData.coordinates;
    let address = "";

    if (sellerDetail.shipping_address.length > 0) {
      coordinates.lat = sellerDetail?.shipping_address[0]?.coordinates?.lat;
      coordinates.lng = sellerDetail?.shipping_address[0]?.coordinates?.lng;
      address = sellerDetail?.shipping_address[0]?.address;
    } else {
      coordinates.lat = 13.6940782;
      coordinates.lng = 100.6022862;
    }

    setAddressData({
      ...addressData,
      coordinates: coordinates,
      address: address,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerDetail]);

  const createOrder = async () => {
    try {
      setIsLoading(true);
      const { data: res } = await updateSellerLocation({
        seller_id: sellerDetail?._id,
        coordinates: addressData.coordinates,
        address: addressData.address,
      });
      if (!!res) {
        enqueueSnackbar(`บันทึกสำเร็จ`, {
          variant: "success",
          autoHideDuration: 2000,
        });
        onSuccess();
      }
    } catch (error) {
      console.error(error.response.data);
      error.response.data.errors.forEach((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
          autoHideDuration: 2000,
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      title: `คุณต้องการออกจากหน้านี้ใช่หรือไม่`,
      onConfirm: () => {
        onClose();
      },
      maxWidth: 342,
    });
  };

  return (
    <Dialog
      onClose={handleOnClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: 800,
            borderRadius: 4,
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <WrapperTitle>จัดการตำแหน่งที่ตั้งบริษัท</WrapperTitle>

        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ py: 2 }}>
          <Grid item xs={12}>
            <WrapperMapsContainer>
              <MapsContainer>
                <CustomMap
                  markerLocation={{
                    lat: addressData?.coordinates?.lat,
                    lng: addressData?.coordinates?.lng,
                  }}
                  changeMarkerLocation={(value) => {
                    let newData = addressData;
                    newData.coordinates = value;
                    setAddressData(newData);
                  }}
                  changeSeletedAddress={(value) => {
                    setAddressData({
                      ...addressData,
                      address: value,
                    });
                  }}
                  inputPlaceholder="ตำแหน่งที่ตั้งบริษัท"
                />
              </MapsContainer>
            </WrapperMapsContainer>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="รายละเอียดที่อยู่"
              variant="outlined"
              fullWidth
              value={addressData?.address}
              onChange={(e) => {
                setAddressData({
                  ...addressData,
                  address: e.target.value,
                });
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {checkAllowPermission("sellers.can_edit") && (
        <DialogActions
          sx={{
            justifyContent: "center",
          }}
        >
          <Button
            variant="secondary"
            // size="large"
            onClick={() => {
              openDialog({
                type: "confirm",
                title: `คุณต้องการบันทึกใช่หรือไม่?`,
                onConfirm: () => {
                  createOrder();
                },
                maxWidth: 342,
              });
            }}
            sx={{
              minWidth: 160,
            }}
            // disabled={!isFormValid}
          >
            บันทึก
          </Button>
        </DialogActions>
      )}

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default AddLocationDialog;
